import { Phone, Trash2, Hammer, Home, Truck, Building2, Recycle } from "lucide-react";
import { PHONE_DISPLAY, PHONE_HREF } from "@/lib/contact";

const BINS = [
    {
        size: "2m³",
        key: "2m3",
        name: "Mini Skip",
        icon: Trash2,
        uses: [
            "Small clean-ups",
            "Garden tidy-ups",
            "Small bathroom jobs",
        ],
        tag: "Residential",
    },
    {
        size: "3m³",
        key: "3m3",
        name: "Skip",
        icon: Home,
        uses: [
            "Garage clear-outs",
            "Small to medium household clean-ups",
            "Kitchen refresh waste",
        ],
        tag: "Residential",
    },
    {
        size: "4m³",
        key: "4m3",
        name: "Skip",
        icon: Truck,
        uses: [
            "Medium renovations",
            "Moving house",
            "Estate clean-ups",
        ],
        popular: true,
        tag: "Residential / Commercial",
    },
    {
        size: "6m³",
        key: "6m3",
        name: "Skip",
        icon: Hammer,
        uses: [
            "Larger home renovations",
            "Bigger commercial jobs",
            "Multi-room clear-outs",
        ],
        tag: "Commercial",
    },
    {
        size: "8m³",
        key: "8m3",
        name: "Skip",
        icon: Building2,
        uses: [
            "Major renovations",
            "Construction sites",
            "Large commercial projects with bulky light waste",
        ],
        tag: "Commercial",
    },
];

export const BinSizes = () => {
    return (
        <section id="bin-sizes" data-testid="section-bin-sizes" className="py-14 md:py-20">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="max-w-2xl tp-reveal">
                    <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-tp-forest font-semibold">
                        <span className="h-1.5 w-1.5 rounded-full bg-tp-gold" />
                        Bin sizes
                    </div>
                    <h2 className="mt-3 font-heading text-3xl md:text-4xl text-tp-forest leading-tight">
                        Choose a bin — we’ll help you pick the right one
                    </h2>
                    <p className="mt-3 text-sm md:text-base text-tp-ink/80">
                        Bins from 2 to 8 cubic metres for skip bin hire across Sydney. Not sure
                        which size suits your job? Give us a call and we’ll sort it in a
                        minute. Heavy waste like soil, bricks and concrete may suit specific
                        bin sizes.
                    </p>
                </div>

                <div className="mt-8 md:mt-10 grid gap-4 md:gap-6 sm:grid-cols-2 lg:grid-cols-3">
                    {BINS.map((bin) => (
                        <article
                            key={bin.key}
                            data-testid={`bin-size-card-${bin.key}`}
                            className="tp-reveal relative bg-white rounded-2xl p-5 md:p-6 border border-tp-forest/10 shadow-[0_10px_30px_rgba(20,83,45,0.08)] hover:shadow-[0_14px_40px_rgba(20,83,45,0.16)] transition-shadow flex flex-col"
                        >
                            {bin.popular && (
                                <span className="absolute -top-3 right-4 rounded-full bg-tp-gold text-tp-forest text-[10px] uppercase tracking-[0.14em] font-bold px-2.5 py-1 shadow">
                                    Most popular
                                </span>
                            )}
                            <div className="flex items-start justify-between gap-3">
                                <div>
                                    <div className="font-heading text-4xl text-tp-forest leading-none">
                                        {bin.size}
                                    </div>
                                    <div className="mt-1 text-sm font-semibold text-tp-ink">
                                        {bin.name}
                                    </div>
                                </div>
                                <span className="inline-flex items-center justify-center h-10 w-10 rounded-lg bg-tp-forest/5 ring-1 ring-tp-gold/40">
                                    <bin.icon size={20} strokeWidth={2.2} className="text-tp-forest" />
                                </span>
                            </div>

                            <div className="mt-3 text-[11px] uppercase tracking-[0.14em] text-tp-forest/80 font-semibold">
                                {bin.tag}
                            </div>

                            <ul className="mt-4 space-y-2 text-sm text-tp-ink/85">
                                {bin.uses.map((u) => (
                                    <li key={u} className="flex items-start gap-2">
                                        <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-tp-recycle shrink-0" />
                                        <span>{u}</span>
                                    </li>
                                ))}
                            </ul>

                            <div className="mt-5 flex flex-col sm:flex-row gap-2 pt-4 border-t border-tp-forest/10">
                                <a
                                    href={PHONE_HREF}
                                    data-testid={`bin-size-call-${bin.key}`}
                                    className="tp-btn-primary text-sm h-11 justify-center flex-1 tp-focus"
                                >
                                    <Phone size={15} strokeWidth={2.6} />
                                    Call for a quote
                                </a>
                                <a
                                    href="#quote"
                                    data-testid={`bin-size-quote-${bin.key}`}
                                    className="tp-btn-ghost text-sm h-11 justify-center border border-tp-forest/15 tp-focus"
                                >
                                    Enquire
                                </a>
                            </div>
                        </article>
                    ))}
                </div>

                <div className="mt-8 flex items-start gap-3 rounded-2xl bg-tp-forest/5 border border-tp-forest/10 p-4 md:p-5">
                    <span className="inline-flex items-center justify-center h-9 w-9 rounded-lg bg-white ring-1 ring-tp-recycle/40 shrink-0">
                        <Recycle size={18} strokeWidth={2.2} className="text-tp-recycle" />
                    </span>
                    <p className="text-sm md:text-base text-tp-ink/85">
                        Call
                        <a
                            href={PHONE_HREF}
                            className="font-semibold underline underline-offset-4 decoration-tp-gold decoration-2 hover:text-tp-forest mx-1"
                            data-testid="bin-sizes-inline-call"
                        >
                            {PHONE_DISPLAY}
                        </a>
                        and we&rsquo;ll help you choose the right size &mdash; no guesswork, no overpaying.
                    </p>
                </div>

                {/* Bin size dimensions diagram */}
                <div
                    id="bin-size-diagram"
                    data-testid="bin-size-diagram"
                    className="tp-reveal mt-8 md:mt-12 bg-white rounded-2xl border border-tp-forest/10 shadow-[0_10px_30px_rgba(20,83,45,0.06)] overflow-hidden"
                >
                    <div className="px-5 md:px-7 pt-5 md:pt-7 pb-3 md:pb-4 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2">
                        <div>
                            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-tp-forest font-semibold">
                                <span className="h-1.5 w-1.5 rounded-full bg-tp-gold" />
                                Bin dimensions guide
                            </div>
                            <h3 className="mt-2 font-heading text-2xl md:text-3xl text-tp-forest leading-tight">
                                Side-view sizing at a glance
                            </h3>
                            <p className="mt-2 text-sm text-tp-ink/75 max-w-xl">
                                Approximate dimensions for each of our skip bins &mdash; from
                                2m&sup3; mini skips through to 8m&sup3; builder bins.
                                Wheelie-bin counts are indicative.
                            </p>
                        </div>
                        <a
                            href={PHONE_HREF}
                            data-testid="bin-diagram-call"
                            className="tp-btn-primary text-sm h-11 self-start sm:self-auto tp-focus"
                        >
                            <Phone size={15} strokeWidth={2.6} />
                            Not sure? Call us
                        </a>
                    </div>
                    <div className="px-3 md:px-5 pb-5 md:pb-6">
                        <picture>
                            <source
                                srcSet="/bin-size-diagram.webp"
                                type="image/webp"
                            />
                            <img
                                src="/bin-size-diagram.jpg"
                                alt="Skip bin size diagrams showing 2m3, 3m3, 4m3, 6m3 and 8m3 skip bins with length and height dimensions"
                                className="w-full h-auto rounded-xl border border-tp-forest/10 bg-tp-offwhite"
                                loading="lazy"
                                decoding="async"
                            />
                        </picture>
                        <p className="mt-3 text-xs text-tp-ink/60">
                            Dimensions are indicative only. Final measurements confirmed
                            when we quote your job.
                        </p>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default BinSizes;
